import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useParams, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getPremiumStatus, getSubscriptionDetails } from "../components/getPremiumStatus";
import { getPortalUrl } from "../components/stripePayments";

export const Subscription = () => {
  const { uid } = useParams();
  const [user] = useAuthState(auth);
  const [premium, setPremium] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      return;
    }
    const fetchUserTier = async () => {
      try {
        const isPrem = await getPremiumStatus(auth.app);
        setPremium(isPrem);

        if (isPrem) {
          const subDetails = await getSubscriptionDetails(auth.app);
          setActiveSubscription(subDetails);
        }
      } catch (err) {
        console.error("fetchUserProfile error:", err);
      }
    };
    fetchUserTier();
  }, [user, uid]);

  const handleViewPricing = () => {
    navigate("/pricing");
  };

  const getProviderInfo = () => {
    const prov = String(
      activeSubscription?.canonicalProvider ||
      activeSubscription?.provider ||
      activeSubscription?.type ||
      ""
    ).toLowerCase();

    const isGooglePlay = prov.includes('google') || prov.includes('android') || prov.includes('play');
    const isApple = prov.includes('apple') || prov.includes('ios');
    const isStripe = prov.includes('stripe') || prov.includes('web');
    const isLifetime = prov.includes('lifetime') || activeSubscription?.plan === 'lifetime';

    let displayName = "Active Subscription";
    if (isApple) displayName = "Apple App Store";
    else if (isGooglePlay) displayName = "Google Play";
    else if (isStripe) displayName = "Flintt Website (Stripe)";
    else if (isLifetime) displayName = "Lifetime Premium";

    return { prov, isGooglePlay, isApple, isStripe, isLifetime, displayName };
  };

  const handleManageSubscription = async (targetProvider = null) => {
    setLoading(true);
    try {
      const { isGooglePlay, isApple, isStripe, isLifetime } = getProviderInfo();
      const provToManage = targetProvider || (isGooglePlay ? 'google' : isApple ? 'apple' : isStripe ? 'stripe' : isLifetime ? 'lifetime' : null);

      if (provToManage === 'google' || (!provToManage && isGooglePlay)) {
        window.open("https://play.google.com/store/account/subscriptions", "_blank");
        setLoading(false);
        return;
      }

      if (provToManage === 'apple' || (!provToManage && isApple)) {
        window.open("https://apps.apple.com/account/subscriptions", "_blank");
        setLoading(false);
        return;
      }

      if (provToManage === 'apple_web') {
        window.open("https://support.apple.com/HT202039", "_blank");
        setLoading(false);
        return;
      }

      if (isLifetime) {
        alert("Your Lifetime plan does not require renewal or cancellation.");
        setLoading(false);
        return;
      }

      // Default to Stripe Customer Portal
      try {
        const createStripePortalLink = httpsCallable(
          functions,
          "createStripePortalLink"
        );
        const result = await createStripePortalLink();
        if (result?.data?.url) {
          window.location.href = result.data.url;
          return;
        }
      } catch (portalErr) {
        console.warn("createStripePortalLink error, attempting fallback portal:", portalErr);
        const url = await getPortalUrl(auth.app, { userDocId: user?.uid, email: user?.email });
        if (url) {
          window.location.href = url;
          return;
        }
        throw portalErr;
      }
    } catch (error) {
      console.error("Error managing subscription:", error);
      alert(error.message || "Failed to open subscription management portal.");
      setLoading(false);
    }
  };

  const getManageButtonText = () => {
    if (loading) return "Redirecting...";
    const { isGooglePlay, isApple, isStripe, isLifetime } = getProviderInfo();
    if (isApple) {
      return "Manage on Apple App Store";
    } else if (isGooglePlay) {
      return "Manage on Google Play";
    } else if (isStripe) {
      return "Manage on Flintt Website (Stripe)";
    } else if (isLifetime) {
      return "Lifetime Plan Active";
    } else {
      return "Manage Subscription";
    }
  };

  const providerInfo = getProviderInfo();

  const formattedExpiry = activeSubscription?.expiresAt
    ? new Date(activeSubscription.expiresAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div className="main settings subscription">
      {premium ? (
        <div>
          <h1>My Subscription</h1>
          <h2>Premium: All features unlocked ✓</h2>
          
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(238, 220, 197, 0.25)',
            borderRadius: '12px',
            padding: '20px',
            margin: '24px auto',
            maxWidth: '440px',
            textAlign: 'left',
            color: '#fff'
          }}>
            <p style={{ margin: '8px 0', fontSize: '15px' }}>
              <strong>Purchased Via: </strong>
              <span style={{ color: '#eedcc5' }}>{providerInfo.displayName}</span>
            </p>
            <p style={{ margin: '8px 0', fontSize: '15px' }}>
              <strong>Plan: </strong>{activeSubscription?.plan ? String(activeSubscription.plan).toUpperCase() : 'PREMIUM'}
            </p>
            {formattedExpiry && (
              <p style={{ margin: '8px 0', fontSize: '15px' }}>
                <strong>{activeSubscription?.autoRenew === false ? 'Active until: ' : 'Next renewal: '}</strong>
                {formattedExpiry}
              </p>
            )}
            {activeSubscription?.autoRenew === false ? (
              <p style={{ color: '#FBEAD3', fontSize: '13px', marginTop: '12px', lineHeight: 1.4 }}>
                Auto-renew is cancelled. You retain active access until {formattedExpiry || 'the end of your billing cycle'}.
              </p>
            ) : (
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginTop: '12px', lineHeight: 1.4 }}>
                Your plan is active and will automatically renew. You can cancel anytime without losing remaining days.
              </p>
            )}

            {/* Cross-Platform Guidance for Web Visitors */}
            {providerInfo.isApple && (
              <div style={{
                marginTop: '14px',
                padding: '10px 12px',
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                borderRadius: '8px',
                borderLeft: '3px solid #c5a059',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  <strong>Apple In-App Purchase Notice:</strong>
                </p>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9 }}>
                  Because your subscription was purchased through Apple, Apple requires cancellations to be made through your Apple ID settings on an Apple device (iPhone, iPad, Mac) or via Apple\'s online support portal.
                </p>
              </div>
            )}

            {providerInfo.isGooglePlay && (
              <div style={{
                marginTop: '14px',
                padding: '10px 12px',
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                borderRadius: '8px',
                borderLeft: '3px solid #c5a059',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  <strong>Google Play Subscription Notice:</strong>
                </p>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9 }}>
                  You can manage, pause, or cancel your subscription directly in Google Play Subscriptions from any browser by signing in with your Google account.
                </p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', marginTop: '12px' }}>
            <button
              className="premium-button"
              onClick={() => handleManageSubscription()}
              disabled={loading}
            >
              {getManageButtonText()}
            </button>

            {providerInfo.isApple && (
              <button
                type="button"
                onClick={() => handleManageSubscription('apple_web')}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(197, 160, 89, 0.6)',
                  color: '#c5a059',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Apple Web Cancellation Guide
              </button>
            )}
          </div>

          {/* Universal Store Links for All Platforms */}
          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', maxWidth: '440px', margin: '30px auto 0' }}>
            <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '10px' }}>
              Subscribed through another store or account?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => handleManageSubscription('google')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Google Play Center
              </button>
              <button
                type="button"
                onClick={() => handleManageSubscription('apple')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Apple Subscriptions
              </button>
              <button
                type="button"
                onClick={() => handleManageSubscription('stripe')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Stripe Portal
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h1>My Subscription</h1>
          <h2>Free: Try premium to unlock all features</h2>
          <div>
            <span>
              <div title="View pricing plans">
                <button
                  className="premium-button"
                  onClick={() => handleViewPricing()}
                >
                  Try Premium
                </button>
              </div>
            </span>
          </div>

          {/* Universal Links for Free Users */}
          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', maxWidth: '440px', margin: '30px auto 0' }}>
            <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '10px' }}>
              Need to manage an active subscription on another store or account?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => handleManageSubscription('google')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Google Play Subscriptions
              </button>
              <button
                type="button"
                onClick={() => handleManageSubscription('apple')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Apple Subscriptions
              </button>
              <button
                type="button"
                onClick={() => handleManageSubscription('stripe')}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Stripe Portal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
